import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { DataGrid, GridColDef, GridSelectionModel } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import {
	IAdminDatasetCompany,
	IAdminDatasetPermission,
	updateAdminDatasetPermissionCompanySubset,
	useAdminDatasetCompaniesQuery,
} from '../../api/admin';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';

interface Props {
	open: boolean;
	onClose: () => void;
	userId: number;
	userEmail: string;
	/** The permission whose company subset is being edited; null while the dialog is closed. */
	permission: IAdminDatasetPermission | null;
}

type Mode = 'subset' | 'full';

const COMPANY_COLUMNS: GridColDef[] = [
	{ field: 'symbol', headerName: 'Symbol', width: 110 },
	{ field: 'name', headerName: 'Company', flex: 1.5, minWidth: 180 },
	{ field: 'sector', headerName: 'Sector', flex: 1, minWidth: 140 },
	{ field: 'industry', headerName: 'Industry', flex: 1, minWidth: 140 },
];

function symbols(companies: IAdminDatasetCompany[]): string {
	return companies.map((c) => c.symbol).join(', ');
}

/**
 * Edits the company subset of one dataset permission. The pool is the dataset's active covered
 * companies; a saved subset must name at least one of them, and "Full universe" clears it.
 */
export default function EditCompanySubsetDialog({ open, onClose, userId, userEmail, permission }: Props) {
	const queryClient = useQueryClient();
	const { data: companies, isLoading } = useAdminDatasetCompaniesQuery(
		open && permission ? permission.dataset_id : null
	);

	const [step, setStep] = useState<'select' | 'confirm'>('select');
	const [mode, setMode] = useState<Mode>('subset');
	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
	const [initialisedFor, setInitialisedFor] = useState<number | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const currentSubset = permission?.company_id_subset ?? null;
	const companyIds = useMemo(() => new Set((companies ?? []).map((c) => c.id)), [companies]);

	// Start from the permission's current subset once per open, after the pool has loaded.
	useEffect(() => {
		if (!open) {
			setInitialisedFor(null);
			return;
		}
		if (!permission || !companies || initialisedFor === permission.id) return;
		setSelectionModel((permission.company_id_subset ?? []).filter((id) => companyIds.has(id)));
		setInitialisedFor(permission.id);
	}, [open, permission, companies, companyIds, initialisedFor]);

	const selectedIds = selectionModel.map(Number);
	const selectedSet = new Set(selectedIds);
	const currentSet = new Set(currentSubset ?? []);
	const added = (companies ?? []).filter((c) => selectedSet.has(c.id) && !currentSet.has(c.id));
	const removed = (companies ?? []).filter((c) => currentSet.has(c.id) && !selectedSet.has(c.id));
	// Subset ids no longer in the dataset's active coverage cannot be shown, so saving drops them.
	const stale = (currentSubset ?? []).filter((id) => !companyIds.has(id));
	const changed =
		currentSubset === null ? selectedIds.length > 0 : added.length + removed.length + stale.length > 0;
	const canSave = selectedIds.length > 0 && changed;

	function resetAndClose() {
		setStep('select');
		setMode('subset');
		setError(null);
		setInitialisedFor(null);
		onClose();
	}

	function review(nextMode: Mode) {
		setMode(nextMode);
		setError(null);
		setStep('confirm');
	}

	async function handleConfirm() {
		if (!permission) return;
		setSubmitting(true);
		setError(null);
		try {
			await updateAdminDatasetPermissionCompanySubset(
				userId,
				permission.id,
				mode === 'full' ? null : selectedIds
			);
			await queryClient.invalidateQueries(['admin-user-dataset-permissions', userId]);
			await queryClient.invalidateQueries(['admin', 'datasets', permission.dataset_id, 'permissions']);
			resetAndClose();
		} catch (e: any) {
			const detail = e?.response?.data?.detail;
			setError(typeof detail === 'string' ? detail : 'Failed to update the company subset.');
		} finally {
			setSubmitting(false);
		}
	}

	const datasetLabel = permission ? `${permission.vendor_name} — ${permission.dataset_name}` : '';
	const currentLabel =
		currentSubset === null ? 'full universe' : `${currentSubset.length} compan${currentSubset.length === 1 ? 'y' : 'ies'}`;

	return (
		<Dialog
			open={open}
			onClose={() => !submitting && resetAndClose()}
			maxWidth="md"
			fullWidth
			PaperProps={{ sx: { backgroundImage: 'none' } }}
		>
			{step === 'select' ? (
				<>
					<DialogTitle>Edit Company Subset</DialogTitle>
					<DialogContent sx={{ pb: 1 }}>
						<Typography variant="body2">
							<strong>{datasetLabel}</strong> for <strong>{userEmail}</strong>
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Currently: {currentLabel}. Companies listed are the active companies with data in this
							dataset.
						</Typography>
						{stale.length > 0 && (
							<Alert severity="warning" sx={{ mt: 1 }}>
								{stale.length} compan{stale.length === 1 ? 'y' : 'ies'} in the current subset{' '}
								{stale.length === 1 ? 'is' : 'are'} no longer in this dataset&apos;s active coverage and
								will be dropped when you save.
							</Alert>
						)}
						<Box sx={{ height: 420, width: '100%', mt: 1 }}>
							<DataGrid
								rows={companies ?? []}
								columns={COMPANY_COLUMNS}
								loading={isLoading}
								density="compact"
								checkboxSelection
								disableSelectionOnClick
								selectionModel={selectionModel}
								onSelectionModelChange={setSelectionModel}
								components={{ Toolbar: AdminQuickFilterToolbar }}
								componentsProps={{ toolbar: { showQuickFilter: true, enableExport: false } }}
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Typography variant="body2" color="text.secondary" sx={{ flex: 1, pl: 1 }}>
							{selectedIds.length} of {companies?.length ?? 0} selected
						</Typography>
						<Button onClick={resetAndClose}>Cancel</Button>
						{currentSubset !== null && (
							<Button variant="outlined" onClick={() => review('full')}>
								Full Universe
							</Button>
						)}
						<Button variant="contained" onClick={() => review('subset')} disabled={!canSave}>
							Review Changes
						</Button>
					</DialogActions>
				</>
			) : (
				<>
					<DialogTitle>{mode === 'full' ? 'Confirm Full Universe' : 'Confirm Company Subset'}</DialogTitle>
					<DialogContent>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						{mode === 'full' ? (
							<Typography gutterBottom>
								Remove the company subset on <strong>{datasetLabel}</strong> for{' '}
								<strong>{userEmail}</strong>? They will see every company in the dataset instead of the
								current {currentLabel}.
							</Typography>
						) : (
							<>
								<Typography gutterBottom>
									Set the company subset on <strong>{datasetLabel}</strong> for{' '}
									<strong>{userEmail}</strong> to <strong>{selectedIds.length}</strong> compan
									{selectedIds.length === 1 ? 'y' : 'ies'} (currently {currentLabel}).
								</Typography>
								{added.length > 0 && (
									<Typography variant="body2" sx={{ mt: 1 }}>
										<strong>Adding ({added.length}):</strong> {symbols(added)}
									</Typography>
								)}
								{removed.length > 0 && (
									<Typography variant="body2" sx={{ mt: 1 }}>
										<strong>Removing ({removed.length}):</strong> {symbols(removed)}
									</Typography>
								)}
								{stale.length > 0 && (
									<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
										{stale.length} compan{stale.length === 1 ? 'y' : 'ies'} no longer in this
										dataset&apos;s active coverage will be dropped.
									</Typography>
								)}
							</>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setStep('select')} disabled={submitting}>
							Back
						</Button>
						<Button
							variant="contained"
							color={mode === 'full' ? 'warning' : 'success'}
							onClick={handleConfirm}
							disabled={submitting}
							startIcon={submitting ? <CircularProgress size={16} /> : undefined}
						>
							{submitting ? 'Saving…' : mode === 'full' ? 'Grant Full Universe' : 'Save Subset'}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
